import { AreaType, UNSPOTTED_TRACK_ID } from "./constants";
// Import filter helpers
import { createFilterParams, carMatchesFilters } from "./TrackFilterParams";

/**
 * Definition for the virtual "unspotted" track used when items are not assigned to a physical track.
 * Atrificail object to mirror aisle structure for display
 */
export const VIRTUAL_UNSPOTTED_TRACK = {
  id: UNSPOTTED_TRACK_ID,
  name: "Unspotted",
  prefix: "",
  locations: {
    items: [
      {
        id: UNSPOTTED_TRACK_ID,
        area: {
          areaType: AreaType.TRACK,
          name: "Tracks",
          prefix: "",
        },
        zone: { name: "Unspotted", prefix: "" },
        aisle: { id: UNSPOTTED_TRACK_ID, name: "Unspotted", prefix: "" },
      },
    ],
  },
  area: { areaType: AreaType.TRACK, name: "Unspotted", prefix: "" },
  zone: { name: "Unspotted", prefix: "" },
};

/**
 * Returns true when a TrackCar is spotted on a physical track.
 * Spotted means active=true and trackId is set to a non-virtual track.
 */
export const isSpotted = (trackCar) =>
  Boolean(
    trackCar &&
      trackCar.active === "true" &&
      trackCar.trackId &&
      trackCar.trackId !== UNSPOTTED_TRACK_ID,
  );

// Module-level state for color maps.
// Managed by ColorMapContext in production; also used when context is not yet available.
let accountColorMap = {};
let productColorMap = {};
let commodityColorMap = {};
let productNameMap = {}; // Map of productId to product name

// Promise queues to ensure atomic updates and prevent race conditions
// Each update waits for the previous one to complete, ensuring no updates are lost
let accountColorMapUpdateQueue = Promise.resolve();
let productColorMapUpdateQueue = Promise.resolve();

/**
 * Clears all color maps. Used for cleanup and preventing memory leaks.
 * Should be called when ColorMapProvider unmounts or when reloading data.
 */
export const clearColorMaps = () => {
  accountColorMap = {};
  productColorMap = {};
  commodityColorMap = {};
  productNameMap = {};
  // Reset queues to fresh promises
  accountColorMapUpdateQueue = Promise.resolve();
  productColorMapUpdateQueue = Promise.resolve();
};

/**
 * Extracts display fields from a TrackCar's inbound/outbound shipment.
 * Keeps raw TrackCar structure intact (no mutation).
 */
export const getTrackCarDisplayFields = (trackCar) => {
  if (!trackCar) {
    return {
      shipment: null,
      accountName: null,
      accountId: null,
      accountColor: null,
      railCarNumber: null,
      productName: null,
      productId: null,
      productColor: null,
      reloadYardCommodityId: null,
    };
  }

  const accountId = trackCar?.accountId || null;
  const railCarNumber = trackCar?.railCarNumber || null;

  const shipment = trackCar.inbound ?? trackCar.outbound;
  const accountName = shipment?.accountName || null;
  const reloadYardCommodityId = shipment?.reloadYardCommodityId || null;
  const accountColor = shipment?.account?.color || null;

  let inventory =
    shipment?.inventories?.items?.[0] ||
    shipment?.shipmentLineItems?.items?.[0]?.inventory ||
    null;
  let productName = null;
  let productId = null;
  let productColor = null;

  if (inventory) {
    productId = inventory.productId || null;
    productName = inventory.productName || null;
    productColor = inventory.product?.color || null;
  }

  return {
    shipment,
    accountName,
    accountId,
    accountColor,
    railCarNumber,
    productName,
    productId,
    productColor,
    reloadYardCommodityId,
  };
};

/**
 * Sets the account color map for deterministic color lookups.
 * Uses a promise queue to ensure atomic updates and prevent race conditions.
 * @param {Object} map - Map of accountId to hex color string
 * @returns {Promise} Promise that resolves when the update is complete
 */
export const setAccountColorMap = (map) => {
  accountColorMapUpdateQueue = accountColorMapUpdateQueue.then(() => {
    accountColorMap = { ...accountColorMap, ...map };
  });
  return accountColorMapUpdateQueue;
};

/**
 * Sets the product color map for deterministic productId-based color lookups.
 * Uses a promise queue to ensure atomic updates and prevent race conditions.
 * @param {Object} map - Map of productId to hex color string
 * @param {Array} products - Array of product objects with id, color, and reloadYardCommodityId
 * @returns {Promise} Promise that resolves when the update is complete
 */
export const setProductColorMap = (map, products = []) => {
  productColorMapUpdateQueue = productColorMapUpdateQueue.then(() => {
    productColorMap = { ...productColorMap, ...map };

    if (products && products.length > 0) {
      const nextCommodityMap = { ...commodityColorMap };
      const nextProductNameMap = { ...productNameMap };
      products.forEach((p) => {
        if (p.reloadYardCommodityId && p.color) {
          // Keep commodity map populated for legacy non-color metadata lookups.
          if (!nextCommodityMap[p.reloadYardCommodityId]) {
            nextCommodityMap[p.reloadYardCommodityId] = p.color;
          }
        }
        // Store product name by productId (store as both string and number to handle type mismatches)
        if (p.id && p.name) {
          nextProductNameMap[p.id] = p.name;
          // Also store as string to handle type mismatches
          nextProductNameMap[String(p.id)] = p.name;
        }
      });
      commodityColorMap = nextCommodityMap;
      productNameMap = nextProductNameMap;
    }
  });
  return productColorMapUpdateQueue;
};

/**
 * Returns the color assigned to an account in the database.
 * No longer generates fallback colors.
 */
export const getAccountColor = (accountId) => {
  if (!accountId) return null;
  return accountColorMap[accountId] || null;
};

/**
 * Returns a color only when a productId exists in the map.
 * No commodity fallback is applied by design.
 */
export const getProductColor = (productId) => {
  if (!productId) return null;
  return productColorMap[productId] || null;
};

/**
 * Returns a product name by productId when it exists in the map (exact or string-normalized id).
 * No commodity-id fallback; that would need a commodity→product map.
 */
export const getProductName = (productId) => {
  if (!productId) return null;

  if (productNameMap[productId]) {
    return productNameMap[productId];
  }

  const productIdStr = String(productId);
  if (productNameMap[productIdStr]) {
    return productNameMap[productIdStr];
  }

  return null;
};

/**
 * Stable sorting for track cars:
 * 1) sortKey (backend ordering)
 * 2) railCarNumber (human-friendly tie-breaker)
 * 3) id (final tie-breaker for stability)
 */
export const sortTrackCars = (list = []) =>
  [...list].sort((a, b) => {
    const keyA = String(a?.sortKey ?? "");
    const keyB = String(b?.sortKey ?? "");
    const sortComparison = keyA.localeCompare(keyB);
    if (sortComparison !== 0) return sortComparison;

    const railA = a?.railCarNumber || "";
    const railB = b?.railCarNumber || "";
    const railCompare = railA.localeCompare(railB);
    if (railCompare !== 0) return railCompare;

    return (a?.id || "").localeCompare(b?.id || "");
  });

/**
 * Returns cars for a specific trackId from a processed map.
 */
export const getCarsForTrack = (processedMap = {}, trackId) =>
  (trackId && processedMap[trackId]) || [];

/**
 * Checks if any car on track is selected using a Set for O(1) lookups.
 */
export const hasSelectedOnTrack = (cars = [], selectedIdsSet = null) => {
  if (!selectedIdsSet || selectedIdsSet.size === 0) return false;
  return cars.some((car) => selectedIdsSet.has(car?.id));
};

/**
 * Builds sortable ids list for dnd-kit.
 */
export const getSortableIds = (cars = []) => cars.map((car) => car.id);

/**
 * Single-pass filtering of track cars by account, product, track IDs, and search.
 * Reduces main thread blocking for large datasets.
 *
 * @param {Object} carsMap - Map of trackId to array of cars
 * @param {Object} filters - Filter criteria
 * @param {Object} prevResults - Previous results to enable referential stability
 * @param {Object} options - Optional flags for expensive outputs
 * @param {boolean} options.buildVisibleCars - Build flattened visibleCars array
 * @param {boolean} options.buildFoundCarMap - Build foundCarMap for search
 * @returns {Object} { visibleCarsMap, visibleCars, foundCarMap }
 */
export const processTrackData = (
  carsMap = {},
  {
    accountId,
    productId,
    reloadYardCommodityId,
    trackIds = [],
    searchTerm,
  } = {},
  prevResults = null,
  { buildVisibleCars = true, buildFoundCarMap = true } = {},
) => {
  const filterParams = createFilterParams({
    accountId,
    productId,
    reloadYardCommodityId,
    trackIds,
    searchTerm,
  });
  const hasAccountFilter = filterParams.hasAccountFilter;
  const hasProductFilter = filterParams.hasProductFilter;
  const hasCommodityFilter = !!filterParams.reloadYardCommodityId;
  const hasTrackFilter = filterParams.hasTrackFilter;
  const hasSearch = filterParams.hasSearch;
  const searchLower = hasSearch ? searchTerm.toLowerCase() : "";

  const visibleCarsMap = {};
  const visibleCars = buildVisibleCars ? [] : prevResults?.visibleCars || [];
  const foundCarMap = buildFoundCarMap ? {} : {};
  const trackIdsSet = hasTrackFilter ? new Set(trackIds) : null;

  const prevVisibleCarsMap = prevResults?.visibleCarsMap || {};

  const entries = Object.entries(carsMap);
  for (let i = 0; i < entries.length; i++) {
    const [trackId, cars] = entries[i];

    // Track filter optimization
    if (hasTrackFilter && !trackIdsSet.has(trackId)) {
      continue;
    }

    // Referential stability: reuse previous filtered arrays when inputs unchanged.
    // If the input array for this track is the same as before,
    // and we are NOT in a search mode (which affects foundCarMap and visibleCars array),
    // we can potentially reuse the previous filtered array.
    // However, foundCarMap and visibleCars are global, so we still need to iterate
    // to build them if search is active or if we need the flattened array.

    const canSkipIteration =
      !hasSearch &&
      !hasAccountFilter &&
      !hasProductFilter &&
      !hasCommodityFilter &&
      !hasTrackFilter;

    if (canSkipIteration && prevVisibleCarsMap[trackId] === cars) {
      visibleCarsMap[trackId] = cars;
      if (buildVisibleCars) visibleCars.push(...cars);
      continue;
    }

    const trackMatches = [];
    for (let j = 0; j < cars.length; j++) {
      const car = cars[j];

      const matchesFilters =
        hasAccountFilter || hasProductFilter || hasCommodityFilter
          ? carMatchesFilters(car, filterParams)
          : true;

      if (matchesFilters) {
        trackMatches.push(car);
        if (buildVisibleCars) visibleCars.push(car);

        // Search Filter
        if (
          hasSearch &&
          buildFoundCarMap &&
          (car.railCarNumber?.toLowerCase().includes(searchLower) ||
            car.inbound?.poNumber?.toLowerCase().includes(searchLower) ||
            car.outbound?.poNumber?.toLowerCase().includes(searchLower))
        ) {
          foundCarMap[car.id] = car;
        }
      }
    }

    // Reuse previous array reference when filtered content is identical (fewer downstream re-renders).
    // This prevents downstream re-renders of memoized TrackBucket components
    const prevMatches = prevVisibleCarsMap[trackId];
    const isIdentical =
      prevMatches &&
      prevMatches.length === trackMatches.length &&
      trackMatches.every((car, idx) => car === prevMatches[idx]);

    if (isIdentical) {
      visibleCarsMap[trackId] = prevMatches;
    } else if (
      trackMatches.length ||
      (hasTrackFilter && trackIdsSet.has(trackId))
    ) {
      visibleCarsMap[trackId] = trackMatches;
    }
  }

  // Final referential stability for aggregated outputs.
  const prevFoundCarMap = prevResults?.foundCarMap || {};
  const isFoundMapIdentical =
    Object.keys(foundCarMap).length === Object.keys(prevFoundCarMap).length &&
    Object.keys(foundCarMap).every(
      (key) => foundCarMap[key] === prevFoundCarMap[key],
    );

  const finalFoundCarMap = buildFoundCarMap
    ? isFoundMapIdentical
      ? prevFoundCarMap
      : foundCarMap
    : {};

  const prevVisibleCars = prevResults?.visibleCars || [];
  const isVisibleCarsIdentical =
    visibleCars.length === prevVisibleCars.length &&
    visibleCars.every((car, idx) => car === prevVisibleCars[idx]);

  const finalVisibleCars = buildVisibleCars
    ? isVisibleCarsIdentical
      ? prevVisibleCars
      : visibleCars
    : prevVisibleCars;

  return {
    visibleCarsMap,
    visibleCars: finalVisibleCars,
    foundCarMap: finalFoundCarMap,
  };
};

/**
 * Removes a track car from a track car map by its trackCarId.
 * Returns a new map with the item removed from any track it was found on.
 * Empty tracks are removed from the map.
 */
export const removeTrackCarFromMap = (processedCarsMap = {}, trackCarId) => {
  if (!trackCarId) return processedCarsMap;

  const nextMap = {};
  Object.entries(processedCarsMap).forEach(([trackId, cars]) => {
    const filtered = (cars || []).filter((car) => car.id !== trackCarId);
    // Preserve track key even if empty to maintain map structure and prevent race conditions
    // where single-car tracks disappear during updates.
    nextMap[trackId] = filtered;
  });
  return nextMap;
};

/**
 * Removes a track car from a track car map by its shipment ID (the `id` field).
 * This is critical for handling items that don't yet have a trackCarId (e.g., from UNSPOTTED).
 * Returns a new map with the item removed from any track it was found on.
 * Preserves empty tracks (unlike removeTrackCarFromMap).
 */
export const removeTrackCarByShipmentId = (
  processedCarsMap = {},
  shipmentId,
) => {
  if (!shipmentId) return processedCarsMap;

  const nextMap = {};
  Object.entries(processedCarsMap).forEach(([trackId, cars]) => {
    const filtered = (cars || []).filter(
      (car) => car.shipmentId !== shipmentId,
    );
    // Preserve track key even if empty to maintain map structure
    nextMap[trackId] = filtered;
  });
  return nextMap;
};

/**
 * Updates a tracks array with new capacity data for specific locations.
 * Pure function that returns a new tracks array.
 *
 * @param {Array} tracks - Current list of tracks
 * @param {Array|Object} updatedLocations - Single location object or array of updated location objects
 * @returns {Array} Updated list of tracks
 */
export const updateTracksWithCapacity = (
  prevTracks = [],
  updatedLocations = [],
) => {
  if (!prevTracks || prevTracks.length === 0 || !updatedLocations) {
    return prevTracks;
  }

  const locations = Array.isArray(updatedLocations)
    ? updatedLocations
    : [updatedLocations];
  if (locations.length === 0) return prevTracks;

  const locationMap = new Map(
    locations.map((location) => [location.id, location]),
  );

  return prevTracks.map((track) => {
    const trackLocation = track.locations?.items?.[0];
    if (trackLocation?.id && locationMap.has(trackLocation.id)) {
      const updated = locationMap.get(trackLocation.id);

      // Update the track and location with new capacity data without mutation
      // ONLY update fields that are actually provided in the subscription payload
      const updates = {};
      if (updated.capacityUsed !== undefined && updated.capacityUsed !== null)
        updates.capacityUsed = updated.capacityUsed;
      if (
        updated.capacityAllocated !== undefined &&
        updated.capacityAllocated !== null
      )
        updates.capacityAllocated = updated.capacityAllocated;
      if (updated.length !== undefined && updated.length !== null)
        updates.length = updated.length;

      if (Object.keys(updates).length === 0) return track;

      return {
        ...track,
        ...updates,
        locations: {
          ...track.locations,
          items: [
            {
              ...track.locations.items[0],
              ...updates,
            },
            ...(track.locations.items.slice(1) || []),
          ],
        },
      };
    }
    return track;
  });
};

/**
 * Determines if a shipment should be masked for a customer user.
 * Customer users can see all cars on tracks for spatial awareness,
 * but sensitive details (account, product, status, etc.) are hidden
 * for cars belonging to other accounts.
 *
 * NOTE: This is UI-level masking only. Full shipment data is still
 * transmitted to the client. This provides casual privacy, not security.
 *
 * @param {Object} shipment - The shipment/car object to check
 * @param {boolean} isCustomer - Whether the current user is a customer
 * @param {string|null} customerAccountId - The customer's account ID
 * @returns {boolean} True if the shipment details should be masked
 */
export const isRestrictedForCustomer = (
  shipment,
  isCustomer,
  customerAccountId,
) => {
  if (!isCustomer || !customerAccountId) return false;
  const shipmentAccountId = shipment?.accountId ?? shipment?.account?.id;

  if (shipmentAccountId == null) return true;
  return String(shipmentAccountId) !== String(customerAccountId);
};

const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const isLikelyHexColorString = (value) =>
  typeof value === "string" && HEX_COLOR_RE.test(value.trim());

export const resolveShipmentBackedAccountColor = (record) => {
  if (!record) return null;
  const fromShipment =
    record.inboundShipment?.account?.color ||
    record.outboundShipment?.account?.color ||
    null;
  if (isLikelyHexColorString(fromShipment)) return fromShipment.trim();
  const fromMap = getAccountColor(record.accountId);
  if (isLikelyHexColorString(fromMap)) return fromMap.trim();
  return null;
};

const getFirstShipmentProductInventory = (record) =>
  record?.inboundShipment?.inventories?.items?.[0] ||
  record?.outboundShipment?.shipmentLineItems?.items?.[0]?.inventory ||
  null;

export const resolveShipmentBackedProductColor = (record) => {
  const inventory = getFirstShipmentProductInventory(record);
  const fromShipment = inventory?.product?.color || null;
  if (isLikelyHexColorString(fromShipment)) return fromShipment.trim();

  const fromMap = getProductColor(inventory?.productId);
  if (isLikelyHexColorString(fromMap)) return fromMap.trim();
  return null;
};

export const getShipmentBackedProductName = (record) => {
  const inventory = getFirstShipmentProductInventory(record);
  return inventory?.productName || getProductName(inventory?.productId);
};
